/*
 * Cart drawer element (derived from Shopify Dawn). renderContents(state, dontOpen) accepts a
 * second argument so soulyshine-drawer.js can refresh the drawer without opening it.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.ssBound) return;
    overlay.dataset.ssBound = 'true';
    overlay.addEventListener('click', () => this.close());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    clearTimeout(this.hideTimer);
    this.style.display = '';
    void this.offsetWidth; // reflow so the slide-in transition starts from the hidden state
    // The animation does not always trigger synchronously; a timeout helps.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    // Trap focus once the slide-in has finished (timed, so a skipped transition never leaves a stale listener).
    clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      if (!this.classList.contains('active')) return;
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (typeof trapFocus === 'function' && container && focusElement) trapFocus(container, focusElement);
    }, 250);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.focusTimer);
    this.classList.remove('active');
    // Belt and braces for mobile Safari: once the slide-out is done, take the backdrop out of the page.
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      if (!this.classList.contains('active')) this.style.display = 'none';
    }, 350);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState, dontOpen) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;
    const sections = (parsedState && parsedState.sections) || {};
    this.getSectionsToRender().forEach((section) => {
      const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!element || typeof sections[section.id] !== 'string') return;
      element.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });
    this.bindOverlay();
    if (!dontOpen) setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// A page restored from the back/forward cache (mobile Safari/Chrome back gesture) must never come
// back with the drawer overlay still open.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('cart-drawer').forEach((drawer) => drawer.close());
  document.body.classList.remove('overflow-hidden');
});
window.addEventListener('pagehide', () => {
  document.querySelectorAll('cart-drawer').forEach((drawer) => drawer.close());
});

if (!customElements.get('cart-drawer-items')) {
  // Line edits are routed through soulyshine-drawer.js; this element only marks the list.
  customElements.define('cart-drawer-items', class CartDrawerItems extends HTMLElement {});
}
